import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logout, selectUser, selectStorageLocation } from "../store/authSlice";
import Images from "../utils/Images";
import axiosInstance from "../utils/Api";
import { formatQuantity, formatUnit, getErrorMessage } from "../utils/format";
import OutletWiseList from "./OutletWiseList";
import QuickActions from "../components/QuickActions";

// ---- API endpoints used on this screen -------------------------------
// GET stock-indent/total-requested-quantity?page=1&limit=500&storageLocation=<id>
//   Cumulative raw-material totals across all indents for the storage
//   location. DISPLAY ONLY — no quantity entry happens on this tab.
//
// Quantity entry + status update now live in the "By indent" tab
// (OutletWiseList), where the logistics user works one indent at a time.
// -----------------------------------------------------------------------
// Assumed response shape (same as the old flow): data.data.data is an ARRAY
// OF GROUPS, one per storage location — { storageLocation,
// storageLocationName, items: [...] }. Items are flattened into one list.
// Item fields used: rawMaterial, name, mainCategoryName, subCategoryName,
// unit, totalRequestedQuantity, indentCount.
// -----------------------------------------------------------------------

const CATEGORY_COLORS = [
    "#00A4E3", "#00A88C", "#E08A3C", "#8D6FE0",
    "#E0607F", "#3D9BB8", "#C99A2E", "#5C7FBF",
];

const getCategoryColor = (categoryName, categoryOrder) => {
    const index = categoryOrder.indexOf(categoryName);
    return CATEGORY_COLORS[index % CATEGORY_COLORS.length];
};

const getItemKey = (item) => item.rawMaterial || item.name;

// "Billu" -> "B", "Jane Doe" -> "JD"
const getInitials = (name) => {
    if (!name) return "?";
    return name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part[0])
        .join("")
        .toUpperCase();
};

const TOAST_STYLES = {
    success: {
        bg: "#E5F7F3",
        border: "#B9E9DF",
        text: "#0A6B58",
        iconBg: "#00A88C",
        icon: (
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6 9 17l-5-5" />
            </svg>
        ),
    },
    error: {
        bg: "#FEF2F2",
        border: "#FEE2E2",
        text: "#EF4444",
        iconBg: "#EF4444",
        icon: <span className="text-white text-xs font-bold leading-none">!</span>,
    },
};

// ---- Pull-to-refresh tuning ----
const PULL_THRESHOLD = 70;
const MAX_PULL = 110;

export default function Home() {
    const navigate = useNavigate();

    // "material" = cumulative totals (read-only)
    // "indent"   = indent-wise pickup, grouped by outlet (editable, see OutletWiseList)
    // const [activeTab, setActiveTab] = useState("material");
    const [activeTab, setActiveTab] = useState("indent")

    // ---- Staff profile menu ----
    const dispatch = useDispatch();
    const staffInfo = useSelector(selectUser); // { _id, name, email, userType, ... }
    const storageLocation = useSelector(selectStorageLocation); // { _id, name, state, city, address }
    const storageLocationId = storageLocation?._id;
    const [showProfileMenu, setShowProfileMenu] = useState(false);
    const profileMenuRef = useRef(null);

    // ---- Toast notifications ----
    const [toast, setToast] = useState(null); // { type: 'success' | 'error', message }
    const toastTimerRef = useRef(null);

    const showToast = (type, message, duration = 3500) => {
        if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
        setToast({ type, message });
        toastTimerRef.current = window.setTimeout(() => {
            setToast(null);
            toastTimerRef.current = null;
        }, duration);
    };

    const dismissToast = () => {
        if (toastTimerRef.current) {
            window.clearTimeout(toastTimerRef.current);
            toastTimerRef.current = null;
        }
        setToast(null);
    };

    useEffect(() => {
        return () => {
            if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
        };
    }, []);

    // Close the profile dropdown on any click outside it.
    useEffect(() => {
        if (!showProfileMenu) return undefined;

        const handleClickOutside = (event) => {
            if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
                setShowProfileMenu(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [showProfileMenu]);

    const handleProfile = () => {
        navigate("/profile");
    };

    const handleLogout = () => {
        setShowProfileMenu(false);
        dispatch(logout()); // clears Redux + localStorage (see store.js)
        navigate("/", { replace: true });
    };

    // ---- Header height (so the sticky search bar sits right under it) ----
    const headerRef = useRef(null);
    const [headerHeight, setHeaderHeight] = useState(168);

    useLayoutEffect(() => {
        const node = headerRef.current;
        if (!node) return undefined;

        const measure = () => setHeaderHeight(node.offsetHeight);
        measure();

        if (typeof ResizeObserver === "undefined") return undefined;
        const observer = new ResizeObserver(measure);
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    // ---- Data (cumulative, read-only) ----
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true); // load on mount
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState("");

    const [searchText, setSearchText] = useState("");
    const [sortBy, setSortBy] = useState("name"); // "name" | "quantity"
    const [collapsedCategories, setCollapsedCategories] = useState(() => new Set());

    const fetchCumulativeItems = async ({ isRefresh = false } = {}) => {
        try {
            if (isRefresh) setRefreshing(true);
            else setLoading(true);

            setError("");

            const params = { page: 1, limit: 500 };
            if (storageLocationId) params.storageLocation = storageLocationId;

            const response = await axiosInstance.get("stock-indent/total-requested-quantity", { params });

            const apiGroups = response?.data?.data?.data;
            const groups = Array.isArray(apiGroups) ? apiGroups : [];

            const flattenedItems = groups
                .flatMap((group) => {
                    const groupItems = Array.isArray(group?.items) ? group.items : [];
                    return groupItems.map((item) => ({
                        ...item,
                        storageLocation: group.storageLocation,
                        storageLocationName: group.storageLocationName,
                    }));
                })
                // Nothing requested = nothing to show.
                .filter((item) => Number(item.totalRequestedQuantity || 0) !== 0);

            setItems(flattenedItems);
        } catch (err) {
            console.log("Cumulative quantity API error:", err?.response || err);
            setError(getErrorMessage(err, "Unable to load the material list. Please try again."));
            setItems([]);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    // Fetch as soon as the screen opens (and if the storage location changes).
    useEffect(() => {
        fetchCumulativeItems();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storageLocationId]);

    const categoryOrder = useMemo(() => {
        const seen = [];
        items.forEach((item) => {
            const category = item.mainCategoryName || "Other";
            if (!seen.includes(category)) seen.push(category);
        });
        return seen;
    }, [items]);

    const filteredAndSortedItems = useMemo(() => {
        const query = searchText.trim().toLowerCase();

        let result = items;

        if (query) {
            result = items.filter((item) => {
                const haystack = [item.name, item.mainCategoryName, item.subCategoryName]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();
                return haystack.includes(query);
            });
        }

        return [...result].sort((a, b) => {
            if (sortBy === "quantity") {
                return Number(b.totalRequestedQuantity || 0) - Number(a.totalRequestedQuantity || 0);
            }
            return (a.name || "").localeCompare(b.name || "");
        });
    }, [items, searchText, sortBy]);

    const groupedByCategory = useMemo(() => {
        const groups = new Map();

        filteredAndSortedItems.forEach((item) => {
            const category = item?.mainCategoryName || "Other";
            if (!groups.has(category)) groups.set(category, []);
            groups.get(category).push(item);
        });

        return categoryOrder
            .filter((category) => groups.has(category))
            .map((category) => ({ category, items: groups.get(category) }));
    }, [filteredAndSortedItems, categoryOrder]);

    const stats = useMemo(
        () => ({
            visibleItems: filteredAndSortedItems.length,
            totalCategories: categoryOrder.length,
            totalRequests: items.reduce((sum, item) => sum + Number(item.indentCount || 0), 0),
        }),
        [items, categoryOrder, filteredAndSortedItems]
    );

    const toggleCategory = (category) => {
        setCollapsedCategories((previous) => {
            const next = new Set(previous);
            if (next.has(category)) next.delete(category);
            else next.add(category);
            return next;
        });
    };

    // ---- Pull-to-refresh (touch) ----
    const [pullDistance, setPullDistance] = useState(0);
    const pullDistanceRef = useRef(0);
    const pullStartYRef = useRef(null);
    const pullingRef = useRef(false);

    const updatePull = (value) => {
        pullDistanceRef.current = value;
        setPullDistance(value);
    };

    useEffect(() => {
        const handleTouchStart = (event) => {
            if (refreshing) return;
            if (window.scrollY > 0) return;
            pullStartYRef.current = event.touches[0].clientY;
            pullingRef.current = true;
        };

        const handleTouchMove = (event) => {
            if (!pullingRef.current || pullStartYRef.current === null) return;

            const deltaY = event.touches[0].clientY - pullStartYRef.current;

            if (deltaY <= 0) {
                updatePull(0);
                return;
            }

            if (window.scrollY > 0) {
                pullingRef.current = false;
                updatePull(0);
                return;
            }

            const resisted = Math.min(MAX_PULL, deltaY * 0.5);
            updatePull(resisted);

            if (resisted > 4) event.preventDefault();
        };

        const handleTouchEnd = () => {
            if (!pullingRef.current) return;
            pullingRef.current = false;
            pullStartYRef.current = null;

            const reached = pullDistanceRef.current >= PULL_THRESHOLD;
            updatePull(0);

            if (reached) {
                setRefreshing(true);
                // Full reload so any newly deployed bundle is picked up.
                window.location.reload();
            }
        };

        window.addEventListener("touchstart", handleTouchStart, { passive: true });
        window.addEventListener("touchmove", handleTouchMove, { passive: false });
        window.addEventListener("touchend", handleTouchEnd, { passive: true });

        return () => {
            window.removeEventListener("touchstart", handleTouchStart);
            window.removeEventListener("touchmove", handleTouchMove);
            window.removeEventListener("touchend", handleTouchEnd);
        };
    }, [refreshing]);

    return (
        <main className="min-h-screen flex justify-center xl-body" style={{ background: "#F4FAFC" }}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;700&family=Inter:wght@400;500;600&family=IBM+Plex+Mono:wght@500&display=swap');
                .xl-display { font-family: 'Space Grotesk', 'Inter', ui-sans-serif, system-ui, sans-serif; }
                .xl-body { font-family: 'Inter', ui-sans-serif, system-ui, sans-serif; }
                .xl-mono { font-family: 'IBM Plex Mono', ui-monospace, monospace; }

                /* ---- Toast ---- */
                @keyframes toast-in {
                    from { opacity: 0; transform: translate(-50%, -14px) scale(0.96); }
                    to { opacity: 1; transform: translate(-50%, 0) scale(1); }
                }
                .toast-enter { animation: toast-in 0.35s cubic-bezier(0.22,1,0.36,1) both; }

                /* ---- Storage-location banner ---- */
                @keyframes loc-pin-pulse {
                    0%, 100% { transform: scale(1); opacity: 0.5; }
                    50% { transform: scale(1.8); opacity: 0; }
                }
                @keyframes loc-banner-in {
                    from { opacity: 0; transform: translateY(6px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .loc-banner-enter { animation: loc-banner-in 0.45s cubic-bezier(0.22,1,0.36,1) both; }
                .loc-pin-pulse { animation: loc-pin-pulse 2.2s ease-out infinite; }

                @media (prefers-reduced-motion: reduce) {
                    .toast-enter, .loc-banner-enter, .loc-pin-pulse {
                        animation: none !important;
                    }
                }
            `}</style>

            {/* ---- Toast notification ---- */}
            {toast && (
                <div
                    className="fixed top-5 left-1/2 z-[200] w-[calc(100%-40px)] max-w-[380px] toast-enter"
                    style={{ transform: "translate(-50%, 0)" }}
                    role="status"
                    aria-live="polite"
                >
                    <div
                        className="rounded-[16px] border px-4 py-3.5 flex items-start gap-2.5 shadow-[0_16px_40px_rgba(4,33,63,0.2)]"
                        style={{
                            background: TOAST_STYLES[toast.type].bg,
                            borderColor: TOAST_STYLES[toast.type].border,
                        }}
                    >
                        <span
                            className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                            style={{ background: TOAST_STYLES[toast.type].iconBg }}
                        >
                            {TOAST_STYLES[toast.type].icon}
                        </span>

                        <p
                            className="xl-body text-[13px] font-semibold leading-snug flex-1"
                            style={{ color: TOAST_STYLES[toast.type].text }}
                        >
                            {toast.message}
                        </p>

                        <button
                            type="button"
                            onClick={dismissToast}
                            aria-label="Dismiss notification"
                            className="shrink-0 w-5 h-5 rounded-full flex items-center justify-center active:scale-90 transition"
                            style={{ color: TOAST_STYLES[toast.type].text, opacity: 0.6 }}
                        >
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                <path d="M18 6 6 18M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>
            )}

            {/* ---- Pull-to-refresh indicator ---- */}
            {(pullDistance > 0 || refreshing) && (
                <div
                    className="fixed top-3 left-1/2 z-[150] pointer-events-none flex justify-center"
                    style={{
                        transform: `translate(-50%, ${Math.max(pullDistance, refreshing ? PULL_THRESHOLD : 0) - 40}px)`,
                        opacity: refreshing ? 1 : Math.min(1, pullDistance / PULL_THRESHOLD),
                        transition: pullingRef.current ? "none" : "transform 0.25s ease, opacity 0.25s ease",
                    }}
                >
                    <div className="w-10 h-10 rounded-full bg-white border border-[#DCEAF1] shadow-[0_10px_24px_rgba(4,33,63,0.22)] flex items-center justify-center">
                        <svg
                            width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#00A4E3" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round"
                            className={refreshing ? "animate-spin" : ""}
                            style={!refreshing ? { transform: `rotate(${Math.min(180, (pullDistance / PULL_THRESHOLD) * 180)}deg)` } : undefined}
                        >
                            <path d="M21 12a9 9 0 1 1-2.64-6.36" />
                            <path d="M21 3v6h-6" />
                        </svg>
                    </div>
                </div>
            )}

            <section className="relative w-full max-w-[430px] md:max-w-[680px] lg:max-w-[860px] min-h-screen bg-[#F4FAFC] shadow-[0_24px_70px_rgba(4,105,143,0.2)]">
                {/* Header */}
                <div
                    ref={headerRef}
                    className="sticky top-0 z-30 rounded-b-[28px]"
                    style={{ background: "linear-gradient(135deg, #04698F 0%, #00A4E3 100%)" }}
                >
                    <div className="flex items-center justify-between px-5 pt-4">
                        <div className="flex items-center gap-3 min-w-0">
                            <div className="w-11 h-11 rounded-[14px] bg-white shrink-0 flex items-center justify-center p-1 shadow-[0_6px_16px_rgba(4,33,63,0.25)]">
                                <img src={Images.logo1} alt="XP StockFlow" className="w-full h-full object-contain rounded-[8px]" />
                            </div>
                            <div className="min-w-0">
                                <p className="xl-display text-white text-[25px] font-bold leading-tight truncate">
                                    XP StockFlow
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                            {/* Staff profile menu — avatar + dropdown with logout */}
                            <div className="relative" ref={profileMenuRef}>
                                <button
                                    type="button"
                                    onClick={() => setShowProfileMenu((previous) => !previous)}
                                    aria-label="Account menu"
                                    aria-expanded={showProfileMenu}
                                    className="w-11 h-11 rounded-full bg-white/15 flex items-center justify-center text-white font-bold text-[16px] active:scale-95 transition"
                                >
                                    {getInitials(staffInfo?.name)}
                                </button>

                                {showProfileMenu && (
                                    <div className="absolute right-0 top-[46px] z-40 w-56 rounded-[18px] bg-white border border-[#DCEAF1] shadow-[0_20px_50px_rgba(4,33,63,0.25)] overflow-hidden">
                                        <div className="px-4 py-3.5 border-b border-[#DCEAF1]">
                                            <p className="xl-body text-[13px] font-bold text-[#08213D] truncate">
                                                {staffInfo?.name || "Unknown user"}
                                            </p>

                                            {staffInfo?.email && (
                                                <p className="xl-body text-[11px] font-medium text-[#5B6B7C] truncate mt-0.5">
                                                    {staffInfo.email}
                                                </p>
                                            )}

                                            {storageLocation?.name && (
                                                <p className="xl-body text-[11px] font-semibold text-[#04698F] truncate mt-1">
                                                    {storageLocation.name}
                                                </p>
                                            )}
                                        </div>

                                        <button
                                            type="button"
                                            onClick={handleProfile}
                                            className="w-full flex items-center gap-2.5 px-4 py-3 text-left text-[13px] font-semibold text-slate-700 hover:bg-slate-100 transition"
                                        >
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M20 21a8 8 0 0 0-16 0" />
                                                <circle cx="12" cy="7" r="4" />
                                            </svg>
                                            Profile
                                        </button>

                                        <button
                                            type="button"
                                            onClick={handleLogout}
                                            className="w-full flex items-center gap-2.5 px-4 py-3 text-left text-[13px] font-semibold text-red-500 active:bg-red-50 transition"
                                        >
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                                                <polyline points="16 17 21 12 16 7" />
                                                <line x1="21" y1="12" x2="9" y2="12" />
                                            </svg>
                                            Log out
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="px-5 pt-4 pb-3">
                        <h1 className="xl-display text-white text-[21px] font-bold leading-tight">
                            Welcome, {staffInfo?.name}
                        </h1>
                        <p className="xl-body text-white/80 text-xs font-medium mt-0.5">
                            {activeTab === "material"
                                ? "Total quantity of each material requested across all indents"
                                : "Enter the quantity you are picking for each indent"}
                        </p>
                    </div>

                    {/* Assigned storage location banner */}
                    <div className="px-5 pb-4">
                        <div className="loc-banner-enter rounded-[18px] bg-white/15 border border-white/25 backdrop-blur-sm px-3.5 py-2.5 flex items-center gap-3">
                            <div className="relative w-9 h-9 shrink-0">
                                <span className="loc-pin-pulse absolute inset-0 rounded-full bg-white/60" />
                                <span className="relative w-9 h-9 rounded-full bg-white flex items-center justify-center shadow-[0_6px_14px_rgba(4,33,63,0.25)]">
                                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#04698F" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M12 21s-7-6.2-7-11a7 7 0 0 1 14 0c0 4.8-7 11-7 11Z" />
                                        <circle cx="12" cy="10" r="2.5" />
                                    </svg>
                                </span>
                            </div>

                            <div className="min-w-0 flex-1">
                                <p className="xl-mono text-[9px] font-medium uppercase tracking-wide text-white/75">
                                    Your storage location
                                </p>
                                <p className="xl-display text-white text-[16px] font-bold leading-tight truncate mt-0.5">
                                    {storageLocation?.name || "Not assigned"}
                                </p>
                                {(storageLocation?.city || storageLocation?.state) && (
                                    <p className="xl-body text-white/75 text-[11px] font-medium leading-tight truncate mt-0.5">
                                        {[storageLocation.city, storageLocation.state].filter(Boolean).join(", ")}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Tabs: By material / By indent */}
                    <div className="px-5 pb-4 flex items-center gap-2">
                        {[
                            { key: "indent", label: "By indent" },
                            { key: "material", label: "By material" },
                        ].map((tab) => (
                            <button
                                key={tab.key}
                                type="button"
                                onClick={() => setActiveTab(tab.key)}
                                className={`xl-body flex-1 h-10 rounded-[13px] text-[12px] font-bold transition ${activeTab === tab.key
                                    ? "bg-white text-[#04698F] shadow-[0_6px_14px_rgba(4,33,63,0.18)]"
                                    : "bg-white/15 text-white"
                                    }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* Waybill tear-strip */}
                    <div className="relative h-0">
                        <div className="absolute left-8 right-8 top-0 border-t-2 border-dashed border-white/35" />
                        <div className="absolute -top-[7px] left-6 w-[14px] h-[14px] rounded-full bg-[#F4FAFC]" />
                        <div className="absolute -top-[7px] right-6 w-[14px] h-[14px] rounded-full bg-[#F4FAFC]" />
                    </div>
                </div>

                {/* Shortcuts: Damage entry / Stock report */}
                {/* <div className="px-5 pt-5">
                    <QuickActions />
                </div> */}

                {activeTab === "indent" ? (
                    <div className="px-5 pt-5 pb-10">
                        {/* Indent-wise pickup: quantity entry + Ready / Partially Ready update */}
                        <OutletWiseList onNotify={showToast} />
                    </div>
                ) : (
                    <>
                        <div className="px-5 pt-5">
                            <QuickActions />
                        </div>
                        {/* Search + sort controls */}
                        <div
                            className="sticky z-20 bg-[#F4FAFC]/95 backdrop-blur-md px-5 pt-4 pb-3 border-b border-[#DCEAF1]"
                            style={{ top: headerHeight }}
                        >
                            <div className="relative">
                                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#00A4E3]">
                                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="11" cy="11" r="7" />
                                        <path d="m21 21-4.3-4.3" />
                                    </svg>
                                </span>

                                <input
                                    type="search"
                                    inputMode="search"
                                    value={searchText}
                                    onChange={(event) => setSearchText(event.target.value)}
                                    placeholder="Search item or category"
                                    className="xl-body w-full h-12 rounded-[16px] bg-white border border-[#DCEAF1] pl-11 pr-10 text-sm font-medium text-[#08213D] placeholder:text-[#9FB4BD] focus:outline-none focus:ring-2 focus:ring-[#00A4E3]/30"
                                />

                                {searchText && (
                                    <button
                                        type="button"
                                        onClick={() => setSearchText("")}
                                        aria-label="Clear search"
                                        className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-[#F1FAFD] text-[#5B6B7C] flex items-center justify-center"
                                    >
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                                    </button>
                                )}
                            </div>

                            <div className="flex items-center justify-between mt-3">
                                <div className="flex items-center gap-2">
                                    <span className="xl-mono text-[10px] font-medium uppercase tracking-wide text-[#5B6B7C]">
                                        Sort
                                    </span>

                                    {[
                                        { key: "name", label: "A–Z" },
                                        { key: "quantity", label: "Qty: high–low" },
                                    ].map((option) => (
                                        <button
                                            key={option.key}
                                            type="button"
                                            onClick={() => setSortBy(option.key)}
                                            className={`xl-body h-8 px-3 rounded-full text-[11px] font-semibold transition ${sortBy === option.key
                                                ? "text-white"
                                                : "bg-white border border-[#DCEAF1] text-[#5B6B7C]"
                                                }`}
                                            style={sortBy === option.key ? { background: "#00A4E3" } : undefined}
                                        >
                                            {option.label}
                                        </button>
                                    ))}
                                </div>

                                <button
                                    type="button"
                                    onClick={() => fetchCumulativeItems({ isRefresh: true })}
                                    disabled={refreshing || loading}
                                    aria-label="Refresh list"
                                    className="w-9 h-9 rounded-full bg-white border border-[#DCEAF1] flex items-center justify-center text-[#00A4E3] active:scale-95 transition disabled:opacity-50"
                                >
                                    <svg
                                        width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                        className={refreshing ? "animate-spin" : ""}
                                    >
                                        <path d="M21 12a9 9 0 1 1-2.64-6.36" />
                                        <path d="M21 3v6h-6" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        {/* Summary bar */}
                        {!loading && !error && items.length > 0 && !searchText && (
                            <div className="px-5 pt-4">
                                <div className="rounded-[20px] bg-white border border-[#DCEAF1] px-4 py-3 flex items-center justify-between">
                                    {[
                                        { value: stats.visibleItems, label: "Items" },
                                        { value: stats.totalCategories, label: "Categories" },
                                        { value: stats.totalRequests, label: "Requests" },
                                    ].map((stat, index) => (
                                        <div key={stat.label} className="flex items-center flex-1">
                                            {index > 0 && <div className="w-px h-8 bg-[#E4F2F7]" />}
                                            <div className="text-center flex-1">
                                                <p className="xl-display text-[20px] font-bold text-[#08213D] leading-none">
                                                    {stat.value}
                                                </p>
                                                <p className="xl-mono text-[9px] font-medium text-[#5B6B7C] mt-1 uppercase tracking-wide">
                                                    {stat.label}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Body */}
                        <div className="px-5 pt-5 pb-10">
                            {loading ? (
                                <div className="rounded-[24px] bg-white border border-[#DCEAF1] p-8 text-center">
                                    <div className="w-10 h-10 mx-auto rounded-full border-[3px] border-[#00A4E3]/20 border-t-[#00A4E3] animate-spin" />
                                    <p className="xl-body mt-4 text-sm font-semibold text-[#08213D]">Loading materials</p>
                                </div>
                            ) : error ? (
                                <div className="rounded-[24px] bg-red-50 border border-red-100 p-6 text-center">
                                    <p className="xl-body text-sm font-semibold text-red-500">{error}</p>
                                    <button
                                        type="button"
                                        onClick={() => fetchCumulativeItems()}
                                        className="xl-body mt-4 px-6 h-11 rounded-[14px] bg-red-500 text-white text-xs font-semibold active:scale-95 transition"
                                    >
                                        Try again
                                    </button>
                                </div>
                            ) : items.length === 0 ? (
                                <div className="rounded-[24px] bg-white border border-[#DCEAF1] p-8 text-center">
                                    <p className="xl-body text-sm font-semibold text-[#08213D]">Nothing requested right now</p>
                                    <p className="xl-body text-xs font-medium text-[#5B6B7C] mt-1">
                                        New requests will show up here automatically
                                    </p>
                                </div>
                            ) : filteredAndSortedItems.length === 0 ? (
                                <div className="rounded-[24px] bg-white border border-[#DCEAF1] p-8 text-center">
                                    <p className="xl-body text-sm font-semibold text-[#08213D]">
                                        No items match &quot;{searchText}&quot;
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => setSearchText("")}
                                        className="xl-body mt-4 px-6 h-11 rounded-[14px] text-white text-xs font-semibold active:scale-95 transition"
                                        style={{ background: "#00A4E3" }}
                                    >
                                        Clear search
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    {groupedByCategory.map(({ category, items: categoryItems }) => {
                                        const isCollapsed = collapsedCategories.has(category);
                                        const color = getCategoryColor(category, categoryOrder);

                                        return (
                                            <div key={category}>
                                                <button
                                                    type="button"
                                                    onClick={() => toggleCategory(category)}
                                                    className="w-full flex items-center justify-between mb-3 active:opacity-70 transition"
                                                >
                                                    <div className="flex items-center gap-2.5">
                                                        <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: color }} />

                                                        <h3 className="xl-display text-[#08213D] font-bold text-base leading-tight text-left">
                                                            {category}
                                                        </h3>

                                                        <span className="xl-mono rounded-full bg-white border border-[#DCEAF1] px-2.5 py-0.5 text-[10px] font-medium text-[#08213D]">
                                                            {categoryItems.length}
                                                        </span>
                                                    </div>

                                                    <svg
                                                        width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5B6B7C" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round"
                                                        className={`transition-transform ${isCollapsed ? "-rotate-90" : ""}`}
                                                    >
                                                        <path d="m6 9 6 6 6-6" />
                                                    </svg>
                                                </button>

                                                {!isCollapsed && (
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                        {categoryItems.map((item) => (
                                                            <div
                                                                key={`${category}-${getItemKey(item)}`}
                                                                className="rounded-[22px] bg-white border border-[#DCEAF1] px-4 py-4"
                                                            >
                                                                <div className="flex items-start justify-between gap-3">
                                                                    <div className="min-w-0 flex-1">
                                                                        <h4 className="xl-body text-[#08213D] font-bold text-[15px] leading-snug break-words">
                                                                            {item.name}
                                                                        </h4>

                                                                        {item.subCategoryName && (
                                                                            <span className="xl-body inline-block mt-1.5 rounded-full bg-[#F1FAFD] border border-[#DCEAF1] px-2.5 py-0.5 text-[10px] font-semibold text-[#5B6B7C]">
                                                                                {item.subCategoryName}
                                                                            </span>
                                                                        )}
                                                                    </div>

                                                                    {Number(item.indentCount) > 0 && (
                                                                        <span className="xl-body shrink-0 rounded-full bg-[#FFF7E6] border border-[#FCE4B4] px-2.5 py-1 text-[10px] font-bold text-[#B26A00] whitespace-nowrap">
                                                                            {item.indentCount} {Number(item.indentCount) === 1 ? "request" : "requests"}
                                                                        </span>
                                                                    )}
                                                                </div>

                                                                <div className="mt-4">
                                                                    <p className="xl-mono text-[9px] font-medium text-[#5B6B7C] uppercase tracking-wide">
                                                                        Total requested
                                                                    </p>
                                                                    <p className="xl-display text-[24px] font-bold text-[#08213D] leading-none mt-1">
                                                                        {formatQuantity(item.totalRequestedQuantity)}
                                                                        <span className="xl-body text-[13px] font-semibold text-[#5B6B7C] ml-1.5">
                                                                            {formatUnit(item.unit)}
                                                                        </span>
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </section>
        </main>
    );
}